import React, {useCallback, useEffect, useState} from 'react';
import {FaChartLine, FaExclamationTriangle, FaSyncAlt} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import * as api from '../api';


const windows = [24, 168, 720];
const emptyResult = {summary: {}, alerts: [], byEvent: [], byLocale: [], byError: [], qualityBands: [], sample: {}};

const formatMs = value => value === null || value === undefined ? '—' : value < 1000 ? `${value} ms` : `${(value / 1000).toFixed(1)} s`;
const formatPercent = value => value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;

const Breakdown = ({title, rows, labelKey, formatLabel = value => value}) => <section className="admin-rep-monitoring__breakdown">
  <h3>{title}</h3>
  {rows.length ? <ul>{rows.map(row => <li key={row[labelKey]}><span>{formatLabel(row[labelKey])}</span><strong>{row.count}</strong><small>{formatPercent(row.rate)}</small></li>)}</ul> : <p>—</p>}
</section>;

const RepMonitoringPanel = ({token}) => {
  const {t} = useTranslation();
  const [hours, setHours] = useState(24);
  const [result, setResult] = useState(emptyResult);
  const [status, setStatus] = useState('loading');

  const load = useCallback(() => {
    if (!token) return;
    setStatus('loading');
    api.adminGetRepMonitoring(hours, token).then(response => {
      if (response.status === 200) {
        setResult(response.json);
        setStatus('ready');
      } else setStatus('error');
    });
  }, [hours, token]);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 60000);
    return () => window.clearInterval(interval);
  }, [load]);

  const summary = result.summary || {};
  const alertLabel = alert => t(`REP_MONITOR_ALERT_${String(alert.code || '').toUpperCase()}`, {value: alert.value});
  const codeLabel = (prefix, value) => {
    const key = `${prefix}_${String(value || '').toUpperCase()}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };
  return <section className="admin-rep-monitoring" aria-busy={status === 'loading'}>
    <header>
      <div><span><FaChartLine/> {t('REP_MONITORING')}</span><h2>{t('REP_MONITORING_TITLE')}</h2><p>{t('REP_MONITORING_INTRO')}</p></div>
      <div className="admin-rep-monitoring__controls">
        <label><span>{t('REP_MONITORING_WINDOW')}</span><select value={hours} onChange={event => setHours(Number(event.target.value))}>{windows.map(value => <option key={value} value={value}>{t(`REP_MONITORING_WINDOW_${value}`)}</option>)}</select></label>
        <button type="button" onClick={load} disabled={status === 'loading'} title={t('REFRESH')}><FaSyncAlt/>{t('REFRESH')}</button>
      </div>
    </header>
    {status === 'error' ? <div className="admin-rep-monitoring__error" role="alert">{t('REP_MONITORING_LOAD_ERROR')}</div> : null}
    {(result.alerts || []).length ? <div className="admin-rep-monitoring__alerts">{result.alerts.map(alert => <div key={alert.code} className={`is-${alert.severity}`}><FaExclamationTriangle/><span>{alertLabel(alert)}</span></div>)}</div> : status === 'ready' ? <div className="admin-rep-monitoring__healthy">{t('REP_MONITORING_NO_ALERTS')}</div> : null}
    <div className="admin-rep-monitoring__kpis">
      <div><span>{t('REP_MONITORING_REQUESTS')}</span><strong>{summary.total || 0}</strong></div>
      <div><span>{t('REP_MONITORING_SUCCESS')}</span><strong>{formatPercent(summary.successRate)}</strong></div>
      <div><span>{t('REP_MONITORING_ERRORS')}</span><strong>{formatPercent(summary.errorRate)}</strong></div>
      <div><span>{t('REP_MONITORING_QUALITY')}</span><strong>{summary.averageQualityScore === null || summary.averageQualityScore === undefined ? '—' : `${summary.averageQualityScore}/100`}</strong></div>
      <div><span>{t('REP_MONITORING_VOICE_CONFIDENCE')}</span><strong>{formatPercent(summary.averageVoiceConfidencePct)}</strong></div>
      <div><span>{t('REP_MONITORING_P95')}</span><strong>{formatMs(summary.p95DurationMs)}</strong></div>
    </div>
    <div className="admin-rep-monitoring__breakdowns">
      <Breakdown title={t('REP_MONITORING_BY_EVENT')} rows={result.byEvent || []} labelKey="eventType" formatLabel={value => codeLabel('REP_MONITOR_EVENT', value)}/>
      <Breakdown title={t('REP_MONITORING_BY_LOCALE')} rows={result.byLocale || []} labelKey="locale"/>
      <Breakdown title={t('REP_MONITORING_BY_QUALITY')} rows={result.qualityBands || []} labelKey="band" formatLabel={value => codeLabel('REP_MONITOR_QUALITY', value)}/>
      <Breakdown title={t('REP_MONITORING_TOP_ERRORS')} rows={result.byError || []} labelKey="errorCode"/>
    </div>
    <footer><span>{t('REP_MONITORING_PRIVACY')}</span>{result.sample && result.sample.truncated ? <strong>{t('REP_MONITORING_SAMPLE_TRUNCATED')}</strong> : null}</footer>
  </section>;
};

export default RepMonitoringPanel;
